import { useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { NavLink } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import UrgentMessageDisplay from "../../components/UrgentMessageDisplay";
import { WEEKLY_SCHEDULE } from "../../helpers/upcomingServices";
import { ADDRESS_MAPS_URL } from "../../menu/menuShared";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";
import {
  weeklyWorshipEyebrowString,
  serviceTimesHeadingString,
  getDirectionsString,
  watchServiceString,
  servicePath,
  street,
  city,
  state,
  zip,
} from "../../constants";

const ServiceTimes = () => {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
  };

  const headingStyle = {
    fontFamily: theme.typography.secondaryFontFamily,
    fontWeight: 600,
    color: theme.palette.primary.dark,
    fontSize: "min(9vw, 40px)",
    margin: "0 0 12px",
  };

  const cardStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "20px 24px",
    backgroundColor: theme.palette.brand.linen,
    borderRadius: "10px",
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.default,
        fontFamily: theme.typography.fontFamily,
        paddingTop: "var(--nav-height)",
        // calc, not a bare 100vh: paddingTop above already reserves the
        // header's own space, so this only needs to fill what's left of
        // the screen - a bare 100vh here would add the header's height a
        // second time and force a scrollbar on every load, even when the
        // schedule itself is short enough to fit on one screen.
        minHeight: "calc(100vh - var(--nav-height))",
      }}
    >
      <UrgentMessageDisplay page="ServiceTimes" />

      <div style={{ maxWidth: "720px", margin: "0 auto", padding: "36px 24px 64px" }}>
        <div style={eyebrowStyle}>{weeklyWorshipEyebrowString(language)}</div>
        <h1 style={headingStyle}>{serviceTimesHeadingString(language)}</h1>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "24px" }}>
          {WEEKLY_SCHEDULE.map((service, index) => (
            <div key={index} style={cardStyle}>
              <div
                style={{
                  fontFamily: theme.typography.paragraphFontFamily,
                  fontSize: "16px",
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {service.nameString(language)}
              </div>
              <div
                style={{
                  fontFamily: theme.typography.secondaryFontFamily,
                  fontSize: "22px",
                  fontWeight: 600,
                  color: theme.palette.primary.dark,
                  whiteSpace: "nowrap",
                }}
              >
                {service.displayTime}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            marginTop: "40px",
            paddingTop: "24px",
            borderTop: `1px solid ${theme.palette.brand.border}`,
          }}
        >
          <Button
            variant="contained"
            component={NavLink}
            to={servicePath}
            startIcon={<PlayCircleOutlineIcon />}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.default,
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              textTransform: "none",
              padding: "10px 22px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {watchServiceString(language)}
          </Button>
          <Button
            variant="outlined"
            href={ADDRESS_MAPS_URL}
            target="_blank"
            rel="noreferrer"
            startIcon={<LocationOnIcon />}
            sx={{
              borderColor: theme.palette.brand.brass,
              borderWidth: "1.5px",
              color: theme.palette.primary.dark,
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              textTransform: "none",
              padding: "10px 22px",
              "&:hover": {
                borderColor: theme.palette.brand.brass,
                borderWidth: "1.5px",
                backgroundColor: "rgba(176, 138, 86, 0.15)",
              },
            }}
          >
            {street}, {city}, {state} {zip} · {getDirectionsString(language)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceTimes;
